import { forwardRef, useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { KeyboardEvent, MouseEvent, TextareaHTMLAttributes } from 'react';

import { Field } from '@base-ui/react/field';
import { ArrowUpIcon } from '@phosphor-icons/react/ArrowUp';
import { textInputHelperRecipe } from 'components/TextInput';
import { cn } from 'components/utils';

import {
  chatInputBoxRecipe,
  chatInputControlRecipe,
  chatInputRootRecipe,
  chatInputSendRecipe,
} from './ChatInput.recipe';

import type { ChatInputProps } from './types';

/** One 20px line plus the 16px the control pays itself — the height at which a second line starts. */
const SINGLE_LINE_HEIGHT = 36;

/**
 * The width the control has in the PILL shape, which is the one width the shape decision is allowed
 * to depend on. See the long note in `measure`.
 *
 * Derived from the box rather than from the control, so it answers the same in both shapes: the
 * control's own width is 52px larger in the multi-line box, which is the whole problem. What the
 * box knows is invariant — its inner width, the button it contains, and the gap between them.
 *
 * Returns `null` rather than a number whenever the answer cannot be trusted: no layout engine
 * (happy-dom reports every box as 0), a container too narrow to seat the button, or a missing part.
 * Every caller treats `null` as "leave the shape alone", which is the same call the `scrollHeight`
 * zero-check makes — a guess here would flicker exactly where this function exists to stop it.
 */
const pillControlWidth = (control: HTMLTextAreaElement): number | null => {
  const box = control.closest<HTMLElement>('[data-part="box"]');
  const send = box?.querySelector<HTMLElement>('[data-part="send"]');

  if (!box || !send) return null;

  const { paddingLeft, paddingRight, columnGap } = getComputedStyle(box);
  const inner = box.clientWidth - parseFloat(paddingLeft) - parseFloat(paddingRight);

  // `gap-[4px]` sets both axes, so the column gap reads 4px in the multi-line column too. A
  // non-finite parse means the shorthand resolved to `normal`, which is zero for flex.
  const gap = Number.parseFloat(columnGap);
  const width = inner - send.offsetWidth - (Number.isFinite(gap) ? gap : 0);

  return Number.isFinite(width) && width > 0 ? width : null;
};

/**
 * Design System 2.0 chat composer.
 *
 * Ported from Figma node 1137:27674 (`Chat`), which is a SEPARATE component set from both
 * `TextInput` and `TextInputPill` rather than a variant of either:
 *
 *   - 56px tall at rest against 60px and 42px, and it GROWS, which neither of the others does.
 *   - a `<textarea>`, not an `<input>`. Two reasons, and only one of them is the newline: an
 *     `<input>` renders on a single line and does not WRAP, so it cannot draw the multi-line shape
 *     at all. That holds even for a consumer that sets `allowNewlines={false}` — see the note
 *     there — which is why disabling newline entry does not make this an `<input>`.
 *   - an inline send button, which is what makes it a composer rather than a field.
 *   - no floating label. The "Label" in the design sits on the value line and does not move, which
 *     makes it a placeholder; the accessible name therefore comes from a visually hidden
 *     `Field.Label`, exactly as on `TextInputPill`.
 *   - helper text below the border, and no focus halo — this set draws a 1px focus border and
 *     nothing else (node 1137:45012).
 *
 * Theme is not a prop: colours resolve from the nearest `.dark` ancestor through the token layer, so
 * the same markup is correct in both themes.
 *
 * `State` and `Content` are observed from the DOM rather than passed, because Base UI's `Field`
 * already tracks them. `Multi-line` is the one axis that cannot be — see `multiline` in `types.ts`.
 */
export const ChatInput = forwardRef<HTMLTextAreaElement, ChatInputProps>(
  (
    {
      label,
      placeholder,
      helperText,
      error,
      disabled,
      multiline,
      allowNewlines = true,
      onSend,
      sendDisabled,
      sendLabel = 'Send',
      className,
      rootClassName,
      onKeyDown,
      value,
      ...rest
    },
    ref
  ) => {
    const controlRef = useRef<HTMLTextAreaElement | null>(null);
    const [isWrapped, setIsWrapped] = useState(false);

    // Declared above the effects rather than beside the render, because the measuring effect
    // depends on it — see the note there.
    const isMultiline = multiline ?? isWrapped;

    /**
     * Autosize, and the shape measurement that rides along with it.
     *
     * The height is reset to `auto` before `scrollHeight` is read, or the element reports the height
     * it was last given and the box can only ever grow — the classic autosize bug, which looks like
     * a field that refuses to shrink when text is deleted.
     *
     * `useLayoutEffect` rather than `useEffect` so the new height lands before paint. With
     * `useEffect` a fast typist sees the box lag a frame behind the caret at every line break.
     *
     * happy-dom reports `scrollHeight === 0`, so under unit tests this settles on the pill and stops
     * — which is why the growth behaviour is proved in a `play` story against real Chromium instead.
     * The `multiline` prop is the escape hatch that keeps the shape assertable in a unit test.
     *
     * ── Why the shape is measured at a width the element does not currently have ────────────────
     *
     * Because the two shapes give the text DIFFERENT widths, so measuring at the current one makes
     * the shape depend on itself. In the pill the control is `flex-1` beside the 48px send button;
     * in the multi-line box the button moves to its own row and the control becomes `w-full`, 52px
     * wider. Text whose natural width lands between those two has no stable answer: it wraps as a
     * pill, so the box grows; the grown box fits it on one line, so the box shrinks; and the shrunk
     * box wraps it again. Each flip changes the control's width, which wakes the observer below,
     * which measures again — a visible flicker, at a band of lengths about 52px wide.
     *
     * So the question asked here is a fixed one — *would this text fit on one line in the PILL?* —
     * and the answer no longer depends on which shape happens to be rendered. That is also the
     * question the user is really asking: the big box appears exactly when the message outgrows the
     * one-line composer.
     *
     * `flex` has to be neutralised along with the width: `flex-1` is `flex: 1 1 0%`, and a
     * flex-basis of 0 plus grow decides the used width regardless of any `width` we set. Setting
     * width alone measures the unchanged element and silently changes nothing.
     *
     * The reference width is read from the DOM rather than hard-coded as 52px so it tracks the
     * recipe: the button's size and the box's padding and gap all live in CSS, and a token change
     * that moved any of them would otherwise put the measurement quietly out of step.
     */
    const measure = useCallback(() => {
      const el = controlRef.current;
      if (!el) return;

      const pillWidth = pillControlWidth(el);

      if (pillWidth !== null) {
        const saved = { width: el.style.width, flex: el.style.flex, height: el.style.height };

        el.style.flex = '0 0 auto';
        el.style.width = `${pillWidth}px`;
        el.style.height = 'auto';
        const pillHeight = el.scrollHeight;

        // Restored synchronously, so the observer below never sees the reference width: a
        // ResizeObserver reports the size at delivery time, not every size the element passed
        // through inside one task.
        el.style.width = saved.width;
        el.style.flex = saved.flex;
        el.style.height = saved.height;

        if (pillHeight > 0) {
          setIsWrapped(pillHeight > SINGLE_LINE_HEIGHT);
        }
      }

      /**
       * The height, measured at the width the element ACTUALLY has.
       *
       * A separate reading from the one above, and it has to be: the shape decides the width, so a
       * height taken at the reference width would be a line too tall for the multi-line box. This
       * runs again when `isMultiline` changes — see the effect — which is safe now that the
       * decision above cannot change with it.
       */
      el.style.height = 'auto';
      const { scrollHeight } = el;

      // A zero means "no layout engine" (happy-dom), not "no content". Leave the last good state.
      if (scrollHeight === 0) return;

      el.style.height = `${scrollHeight}px`;
    }, []);

    /**
     * `isMultiline` is a dependency because the shape decides the control's width, and the HEIGHT
     * has to be re-read once the new shape has laid out — otherwise the box keeps the height it was
     * given for the old width, which is a line too tall or too short for one frame.
     *
     * This only terminates because the shape decision inside `measure` is taken at a fixed
     * reference width. Were it taken at the current width, this dependency would turn the flicker
     * described there from a race into an unconditional infinite loop.
     */
    useLayoutEffect(() => {
      measure();
    }, [measure, value, isMultiline]);

    /**
     * A width change re-wraps the text without the value changing, so the effect above cannot see
     * it. The broadcast panel is resizable and switches between a desktop rail and a mobile
     * fullscreen dialog, so this is the common case rather than an edge one.
     *
     * Only the WIDTH is acted on, because `measure` writes the element's HEIGHT: reacting to every
     * resize would mean measure sets a height, the height change notifies the observer, and the
     * observer measures again. It settles on a fixed point in practice, but "in practice" is what
     * `ResizeObserver loop completed with undelivered notifications` is made of.
     *
     * The width filter is NOT itself a loop guard, and an earlier version of this comment claimed
     * it was — "the only dimension this component never writes". The component does write this
     * element's width, indirectly: switching shape moves the control between `flex-1` and `w-full`,
     * which is a 52px change. So a width-triggered remeasure is exactly how the shape flicker kept
     * itself going once a bad measurement started it. What stops that is the fixed reference width
     * in `measure`, which makes a remeasure here idempotent rather than alternating.
     */
    useLayoutEffect(() => {
      const el = controlRef.current;
      if (!el || typeof ResizeObserver === 'undefined') return;

      let lastWidth = el.getBoundingClientRect().width;

      const observer = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width ?? lastWidth;
        if (width === lastWidth) return;

        lastWidth = width;
        measure();
      });
      observer.observe(el);

      return () => {
        observer.disconnect();
      };
    }, [measure]);

    const canSend = !disabled && !sendDisabled;

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
      onKeyDown?.(event);

      /**
       * Enter sends, and Shift+Enter inserts a newline — the chat convention, and the only keyboard
       * route into the multi-line shape.
       *
       * Unless `allowNewlines` is false, in which case Shift is ignored and every Enter sends. The
       * `preventDefault()` below is what actually suppresses the character: without it the textarea
       * would insert the newline first and the send would race it. See the prop's own note for why
       * this closes the keyboard route and not the paste one.
       *
       * `nativeEvent.isComposing` is not optional politeness: while an IME candidate window is open,
       * Enter COMMITS the candidate and the browser reports it as a normal Enter. Without this guard
       * a Japanese or Chinese speaker cannot type a word without sending it half-finished. It is
       * checked on BOTH paths — a consumer that has disabled newlines has not stopped using an IME.
       *
       * `defaultPrevented` is checked so a caller's own `onKeyDown` can opt out of sending, rather
       * than having to duplicate this handler to change one key.
       */
      const insertsNewline = event.shiftKey && allowNewlines;

      if (event.key !== 'Enter' || insertsNewline || event.nativeEvent.isComposing || event.defaultPrevented) {
        return;
      }

      event.preventDefault();

      if (canSend) {
        onSend();
      }
    };

    /**
     * The box's whole surface focuses the control.
     *
     * `TextInput` gets this geometrically, by paying its padding from inside the input; this
     * component cannot, because the send button is a flex sibling rather than an overlay — see the
     * padding note in `ChatInput.recipe`. Doing it explicitly also covers the gap BETWEEN the
     * control and the button, which no padding on the control could reach.
     *
     * `mousedown` rather than `click`, and prevented, so the caret does not first land somewhere
     * else and then jump; and scoped to non-interactive targets so it cannot steal the button's own
     * press.
     */
    const handleBoxMouseDown = (event: MouseEvent<HTMLDivElement>) => {
      const el = controlRef.current;
      if (!el || disabled || event.defaultPrevented) return;
      if (event.target !== event.currentTarget) return;

      event.preventDefault();
      el.focus();
    };

    /**
     * Typed separately because `render` takes a plain element: `rest` is already narrowed to
     * textarea props by `ChatInputProps`, but TypeScript cannot see that through the element's own
     * prop position without the annotation.
     */
    const controlProps: TextareaHTMLAttributes<HTMLTextAreaElement> = rest;

    return (
      // `invalid` is what puts `data-invalid` on the root and `aria-invalid` on the control, so the
      // red border and the announced state cannot drift apart.
      <Field.Root className={cn(chatInputRootRecipe(), rootClassName)} disabled={disabled} invalid={error}>
        {/* Hidden rather than absent: `sr-only` keeps the control named for assistive technology
            while the design shows only the placeholder. */}
        <Field.Label className="sr-only">{label}</Field.Label>

        {/* `data-part` names the box so a parent or a test can target it without depending on a
            styling class, and `data-multiline` publishes the measured shape for the same reason. */}
        <div
          className={cn(chatInputBoxRecipe({ multiline: isMultiline }), className)}
          data-part="box"
          data-multiline={isMultiline ? '' : undefined}
          onMouseDown={handleBoxMouseDown}
        >
          <Field.Control
            ref={ref}
            render=<textarea
              ref={controlRef}
              rows={1}
              className={chatInputControlRecipe({ multiline: isMultiline })}
              placeholder={placeholder ?? label}
              value={value}
              onKeyDown={handleKeyDown}
              {...controlProps}
            />
          />

          <button
            type="button"
            className={chatInputSendRecipe()}
            disabled={!canSend}
            aria-label={sendLabel}
            data-part="send"
            /* Wrapped rather than passed straight through: `onSend` takes no arguments, and handing
               a caller a MouseEvent it never asked for is how an event object ends up somewhere that
               expects something else — the same trap `ChatModerationSwitch` documents for Base UI's
               `onCheckedChange`. */
            onClick={() => {
              onSend();
            }}
          >
            <ArrowUpIcon size={20} />
          </button>
        </div>

        {/**
         * Helper text sits BELOW the border for this set (node 1137:28178), so it is a sibling of
         * the box rather than a child — which is why the slot class is on the root.
         *
         * `textInputHelperRecipe` is reused rather than re-authored — the indent, the size and the
         * flip to the invalid colour off `data-invalid` are the same rule this design needs — but
         * through a `chat` SHAPE, which is what makes it read this component's own slot family.
         * Sharing the pill's shape read the pill's family, and once the families were split that
         * left the error description in inherited colour with nothing but a computed-style
         * assertion able to see it.
         *
         * `Field.Error` needs `match` to render off a caller-supplied flag — without it, it renders
         * only for Base UI's own constraint validation, which this component does not drive.
         */}
        {helperText && error && (
          <Field.Error className={textInputHelperRecipe({ shape: 'chat' })} match>
            {helperText}
          </Field.Error>
        )}
        {helperText && !error && (
          <Field.Description className={textInputHelperRecipe({ shape: 'chat' })}>{helperText}</Field.Description>
        )}
      </Field.Root>
    );
  }
);
